'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { type User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type React from 'react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/providers/RouterShim';
import { getMarketCode, getOidcSettings } from '@/utils/env';
import type { RequestState } from '../@types/RequestState';
import type { Token } from '../@types/Token';
import { BRANCH_QUERY } from '../data/branch/useBranch';
import { BRANCHES_QUERY } from '../data/branch/useBranches';
import { postJSON } from '../fetch';
import useSnackbar from '../state/Snackbar/useSnackbar';
import Loader from '../styled-components/components/Loader';
import { Root, RootHeader, RootLoaderCenter, RootMain } from '../styled-components/components/Root';
import { getExternalView } from '../utils/market';

const REDIRECT_PATH = '/oidc/signin';
const REDIRECT_URL_KEY = 'redirectUrl';
const REQUIRED_SCOPES = 'openid profile api-resource offline_access'.split(' ').sort().join(' ');

// Lazy singleton for UserManager - only created on client side
let userManagerInstance: UserManager | null = null;
let oidcConfig: ReturnType<typeof getOidcSettings> | null = null;

function getOidcConfig() {
  if (typeof window === 'undefined') return null;
  if (!oidcConfig) {
    oidcConfig = getOidcSettings();
  }
  return oidcConfig;
}

function getUserManager(): UserManager | null {
  if (typeof window === 'undefined') return null;

  if (!userManagerInstance) {
    const settings = getOidcConfig();
    if (!settings) return null;

    const issuerItem = settings.validIssuers.find((x) => x.marketCode === getMarketCode());
    const CLIENT_ROOT = window.location.origin;

    userManagerInstance = new UserManager({
      authority: issuerItem?.issuer || '',
      client_id: settings.clientId,
      redirect_uri: `${CLIENT_ROOT}${REDIRECT_PATH}`,
      post_logout_redirect_uri: `${CLIENT_ROOT}`,
      response_type: settings.responseType,
      scope: settings.scope,
      loadUserInfo: false,
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
      acr_values: 'source:dealer-app',
    });

    userManagerInstance.clearStaleState();
  }

  return userManagerInstance;
}

const currentUrlIsRedirectUrl = () => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith(REDIRECT_PATH);
};

const verifyScopes = (userScopes?: string) =>
  userScopes?.split(' ').sort().join(' ') === REQUIRED_SCOPES;

const signInRedirect = async () => {
  const userManager = getUserManager();
  if (!userManager) return;

  sessionStorage.setItem(
    REDIRECT_URL_KEY,
    window.location.pathname === REDIRECT_PATH ? '/' : window.location.pathname,
  );
  await userManager.signinRedirect();
};

const signInSilent = async () => {
  const userManager = getUserManager();
  if (!userManager) return undefined;

  try {
    const user = await userManager.signinSilent();
    if (!user || !verifyScopes(user.scope)) {
      signInRedirect();
    }
    return user || undefined;
  } catch (e) {
    // biome-ignore lint/suspicious/noConsole: <debug>
    console.error('[AUTH] - Failed to sign in silently', e);
    signInRedirect();
    return undefined;
  }
};

export const refreshAndGetToken = async () => {
  await signInSilent();
  return await getToken();
};

const getUser = async (autoRedirectSignIn = true) => {
  const userManager = getUserManager();
  if (!userManager) return undefined;

  try {
    const user = await userManager.getUser();
    if ((!user || !verifyScopes(user.scope)) && autoRedirectSignIn) {
      signInRedirect();
      return undefined;
    }

    if (user?.expired) {
      const refreshedUser = await signInSilent();
      return refreshedUser;
    }

    return user || undefined;
  } catch (e) {
    // biome-ignore lint/suspicious/noConsole: <debug>
    console.error('[AUTH] - Failed to get user', e);
    signInRedirect();
    return undefined;
  }
};

export interface AuthContextProps {
  initialized: boolean;
  decodedToken?: Token;
  isAdmin?: boolean;
  signOut: () => void;
  getToken: () => Promise<string | undefined>;
}

export const useRefetchToken = (): [() => void, RequestState] => {
  const { t } = useTranslation(['authentication']);
  const client = useQueryClient();
  const { pushSnackbarSuccess, pushSnackbarError } = useSnackbar();
  const mutation = useMutation({
    mutationFn: () => {
      const userManager = getUserManager();
      if (!userManager) return Promise.resolve(null);
      return userManager.signinSilent();
    },
    onSuccess: async (user) => {
      setTimeout(() => {
        if (user) {
          client.invalidateQueries({ queryKey: [BRANCHES_QUERY] });
          pushSnackbarSuccess(t('snackbar.refreshTokenSuccess', { ns: 'authentication' }));
        } else {
          pushSnackbarError(t('snackbar.refreshTokenError', { ns: 'authentication' }));
          signInRedirect();
        }
      }, 200);
    },
    onError: () => {
      pushSnackbarError(t('snackbar.refreshTokenError', { ns: 'authentication' }));
      signInRedirect();
    },
  });

  const { mutate, isPending, isSuccess, isError } = mutation;

  return [() => mutate(), { isLoading: isPending, isSuccess, isError }];
};

export const useTemporaryAccess = (): [(branchId: string) => Promise<void>, RequestState] => {
  const { t } = useTranslation(['authentication']);
  const client = useQueryClient();
  const { pushSnackbarSuccess, pushSnackbarError } = useSnackbar();
  const settings = useMemo(() => getOidcConfig(), []);
  const issuerItem = useMemo(
    () => settings?.validIssuers.find((x) => x.marketCode === getMarketCode()),
    [settings],
  );

  const mutation = useMutation({
    mutationFn: async (branchId: string) => {
      const userManager = getUserManager();
      if (!userManager) return;

      const token = await getToken();
      if (!token || !issuerItem?.issuer) return;
      await postJSON(`/api/idp/${branchId}/temporary-access`, token);
      const user = await userManager.signinSilent();
      if (!user) {
        // Token refresh failed, redirect to login
        signInRedirect();
        return;
      }
      // Wait for React state to update with new token before invalidating queries
      // The UserLoaded event fires synchronously after signinSilent, but React state
      // updates are async, so we need to wait for the next render cycle
      await new Promise((resolve) => setTimeout(resolve, 250));
      client.invalidateQueries({ queryKey: [BRANCHES_QUERY] });
      client.invalidateQueries({ queryKey: [BRANCH_QUERY, branchId] });
    },
    onSuccess: async () => {
      pushSnackbarSuccess(t('snackbar.temporaryAccessSuccess', { ns: 'authentication' }));
    },
    onError: () => {
      pushSnackbarError(t('snackbar.temporaryAccessError', { ns: 'authentication' }));
    },
  });

  const { mutateAsync, isPending, isSuccess, isError } = mutation;

  const addTemporaryAccess = useCallback(
    (branchId: string) => mutateAsync(branchId),
    [mutateAsync],
  );

  return [addTemporaryAccess, { isLoading: isPending, isSuccess, isError }];
};

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

let tokenPromise: Promise<string | undefined> | null = null;

export const getToken = async () => {
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = new Promise(async (resolve) => {
    try {
      const user = await getUser();
      resolve(user?.access_token);
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: <debug>
      console.error('[AUTH] - Failed to get token', e);
      resolve(undefined);
    } finally {
      tokenPromise = null;
    }
  });

  return tokenPromise;
};

const signOut = async () => {
  const userManager = getUserManager();
  if (!userManager) return;

  let id_token_hint: string | undefined;
  try {
    const user = await userManager.getUser();
    id_token_hint = user?.id_token;
  } catch (e) {
    // biome-ignore lint/suspicious/noConsole: <debug>
    console.error('[AUTH] - Failed to sign out', e);
    id_token_hint = undefined;
  }
  userManager.signoutRedirect({
    id_token_hint,
  });
};

const getDecodedToken = (token?: string) => {
  const decodedToken = token ? jwtDecode<Token>(token) : undefined;
  if (!decodedToken) return undefined;

  const transformBranches =
    decodedToken['belongs-to-branch'] && typeof decodedToken['belongs-to-branch'] === 'string';

  return {
    ...decodedToken,
    'belongs-to-branch': transformBranches
      ? [decodedToken['belongs-to-branch'] as unknown as string]
      : decodedToken['belongs-to-branch'],
  };
};

interface AuthProviderProps {
  autoRedirectSignIn?: boolean;
  children: React.ReactNode;
}

const AuthProvider = ({
  autoRedirectSignIn: autoRedirectSignInProp,
  children,
}: AuthProviderProps) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | undefined>();
  const initialized = useRef(false);
  // Track hydration to ensure consistent SSR/client rendering
  const [isHydrated, setIsHydrated] = useState(false);

  // Determine autoRedirectSignIn after hydration to avoid SSR mismatch
  // getExternalView() returns undefined on server, so we use a stable default until hydrated
  const autoRedirectSignIn = useMemo(() => {
    if (!isHydrated) return true; // Show loader during SSR and initial hydration
    if (autoRedirectSignInProp !== undefined) return autoRedirectSignInProp;
    return getExternalView() !== 'operations';
  }, [isHydrated, autoRedirectSignInProp]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    // Only run on client side and only once
    if (initialized.current) return;
    initialized.current = true;

    // Set up debug helpers on window
    if (typeof window !== 'undefined') {
      window.waykeAuth = {
        refreshToken: refreshAndGetToken,
        getToken,
        signOut,
      };
    }

    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    const userManager = getUserManager();
    if (!userManager) return;

    userManager.events.addUserLoaded((user: User) => {
      setToken(user.access_token);
    });

    userManager.events.addSilentRenewError(async () => {
      signInRedirect();
    });

    if (currentUrlIsRedirectUrl()) {
      try {
        await userManager.signinCallback();
        const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY) || '/';
        sessionStorage.removeItem(REDIRECT_URL_KEY);
        navigate(redirectUrl);
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: <debug>
        console.error('[AUTH] - Failed to sign in from callback', e);
        signOut();
      }
    } else {
      // Check external view directly here since this only runs on client
      const shouldAutoRedirect =
        autoRedirectSignInProp !== undefined
          ? autoRedirectSignInProp
          : getExternalView() !== 'operations';
      const user = await getUser(shouldAutoRedirect);
      setToken(user?.access_token);
    }
  };

  const decodedToken = getDecodedToken(token);
  const isAdmin = decodedToken?.role === 'wayke-admin';
  const isInitialized = !!token;
  // Show loader during SSR/initial hydration, then render children based on auth state
  const renderChildren = isInitialized || !autoRedirectSignIn;

  return (
    <AuthContext.Provider
      value={{
        initialized: !!token,
        decodedToken,
        isAdmin,
        signOut,
        getToken,
      }}
    >
      {renderChildren ? (
        children
      ) : (
        <Root>
          <RootHeader />
          <RootMain id="main" tabIndex={-1}>
            <RootLoaderCenter>
              <Loader />
            </RootLoaderCenter>
          </RootMain>
        </Root>
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
